import { money } from './money.js';
import { canTransition } from './state/transition.js';
/**
 * Panel accounts and claims. §6.13 `CLM-001` to `CLM-006`; §13.4 `PK-028`; §15.20.
 *
 * **Scope.** `V1-SPEC.md` §2 puts electronic insurance claim gateways out of v1 ("no market for them
 * in Pakistan"). Nothing here builds, signs or transmits a claim. This module is record-keeping and
 * money: the line-level split between a payer and the customer, the lifecycle of what the payer
 * owes, and the statement and ageing a shopkeeper reads. `CLM-006` makes that a complete product on
 * its own: "Claim capture without claim submission is a valid and shippable state". A claim that
 * is captured, printed, settled monthly and paid by cheque never needs a submission, and every
 * function below works on it.
 *
 * **Two debtors, never one balance.** A panel that owes its share of an order and a customer who
 * owes udhaar (`PK-023`) are different people with different remedies. A panel pays monthly against
 * a statement and can dispute a line; a customer is chased at the counter. Summed, they hide both:
 * the shopkeeper sees one figure that is collectible neither by phone nor by statement. So the two
 * live on different ledger accounts (`PANEL_RECEIVABLE_ACCOUNT`, `CUSTOMER_UDHAAR_ACCOUNT`), the
 * panel's share is "a receivable, not a payment" (§15.20 step 3) and is never counted as tendered,
 * and `combineBalances` refuses to add across debtors. The one legitimate crossing is a panel
 * refusing a line, which §15.20 turns into a customer receivable; that happens only through
 * `transfer_to_customer`, which names the amount and the reason and shows up as an effect.
 *
 * **Rounding.** This module is not one of `MR-04`'s three rounding points. Line prices arrive
 * already rounded (line and order rounding happened upstream, and order discounts are already
 * apportioned to lines per `MR-05`). The only division is a percentage plan, and it splits an
 * existing whole into two parts that sum to it exactly: the payer's part is computed in one
 * direction and the customer takes the remainder, so no minor unit is lost or invented.
 *
 * **Left open by the spec, so taken as parameters and never defaulted.** The PRD defines none of
 * these (the claims module references no appendix) and a guess would be a policy chosen by nobody:
 * - Whether a plan needs verified eligibility and whether it needs an authorisation:
 *   `BenefitPlan.eligibilityPolicy`, `BenefitPlan.authorisationRequired`.
 * - Which way a percentage rounds the payer's share: `CoverageRule.payerShareRounding`.
 * - How many times a denied claim may be rebilled: `ClaimPolicy.maxResubmissions`. Absent means no
 *   limit, which is stated here rather than hidden.
 * - Denial reasons: no enumeration exists (Appendix E has none), so `DenialReason.code` is a
 *   non-empty string the shop or country pack supplies.
 * - Ageing buckets and the clock they run from: `bucketBoundsDays`, `ageFrom`.
 * - How a panel payment is spread across a statement: `PaymentPlan`.
 * - The printed claim form's layout and field list: `claimFormData` returns the data a form needs
 *   and nothing about layout, because the spec gives neither.
 * - Whether a refund "requires" a claim reversal: taken as exactly when the payer's share falls.
 * - The enumerations themselves. Appendix E registers none of them: `Payer.kind`, the allowance
 *   shapes, `Eligibility.status`, `ClaimStatus` and the event kinds are all read off §6.13 and
 *   §15.20 rather than the registry, so Appendix E has five lists to add. `packages/db` keeps the
 *   same lists and a test compares them.
 *
 * **Values that arrive already cast.** A plan's coverage rule, the rule frozen onto an allocation
 * line, an eligibility status and a plan's policy all come back from the database, where `ARC-020`
 * allows a tenant to sit briefly on an adjacent schema version. Every one of them is checked as a
 * positive whitelist, so an unknown value refuses the split rather than falling through to the
 * permissive branch. `allocateCoverage` and `validateRule` carry the `RULE_UNKNOWN`,
 * `ELIGIBILITY_UNKNOWN` and `PLAN_POLICY_UNKNOWN` refusals that this costs.
 */
export class ClaimError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.code = code;
        this.name = 'ClaimError';
    }
}
/** Where each debtor's balance posts. Different accounts, so a trial balance cannot merge them. */
export const PANEL_RECEIVABLE_ACCOUNT = 'insurance_receivable';
export const CUSTOMER_UDHAAR_ACCOUNT = 'accounts_receivable';
/**
 * Every rule reaching this module has been through persistence: it is frozen onto an allocation
 * line and read back later, and `ARC-020` lets a tenant sit briefly on an adjacent schema version,
 * so a `kind` or a rounding direction this build has never heard of can arrive already cast. Both
 * are checked as positive whitelists. Falling through to the permissive branch would turn an
 * unknown rounding direction into a silent "up" and an unknown kind into a `TypeError` thrown from
 * the middle of an order.
 */
function validateRule(rule) {
    switch (rule.kind) {
        case 'excluded':
            return;
        case 'fixed_allowance':
            if (rule.allowanceMinor < 0n)
                throw new ClaimError('RULE_INVALID', 'An allowance cannot be negative.');
            return;
        case 'copay':
            if (rule.copayMinor < 0n)
                throw new ClaimError('RULE_INVALID', 'A copay cannot be negative.');
            return;
        case 'schedule_price':
            if (rule.scheduleMinor < 0n)
                throw new ClaimError('RULE_INVALID', 'A schedule price cannot be negative.');
            return;
        case 'percentage':
            if (!Number.isInteger(rule.basisPoints) || rule.basisPoints < 0 || rule.basisPoints > 10000) {
                throw new ClaimError('RULE_INVALID', 'A percentage is whole basis points from 0 to 10000.');
            }
            if (rule.payerShareRounding !== 'down' && rule.payerShareRounding !== 'up') {
                throw new ClaimError('RULE_INVALID', `A percentage must state which way the payer's share rounds, got ${JSON.stringify(rule.payerShareRounding)}.`);
            }
            return;
        default: {
            const unrecognised = rule;
            throw new ClaimError('RULE_UNKNOWN', `No coverage rule of kind ${JSON.stringify(unrecognised.kind)} exists in this build.`);
        }
    }
}
/** The payer's part of one price. The customer's part is always `price - payer`, so the pair is exact. */
export function payerShareOf(rule, priceMinor) {
    validateRule(rule);
    if (priceMinor < 0n)
        throw new ClaimError('PRICE_NEGATIVE', 'A line price cannot be negative.');
    const lower = (a, b) => (a < b ? a : b);
    switch (rule.kind) {
        case 'excluded':
            return 0n;
        case 'fixed_allowance':
            return lower(rule.allowanceMinor, priceMinor);
        case 'schedule_price':
            return lower(rule.scheduleMinor, priceMinor);
        case 'copay':
            return priceMinor - lower(rule.copayMinor, priceMinor);
        case 'percentage': {
            const numerator = priceMinor * BigInt(rule.basisPoints);
            return rule.payerShareRounding === 'down' ? numerator / 10000n : (numerator + 9999n) / 10000n;
        }
    }
}
/**
 * `CLM-002`: split one order between payer and customer, line by line.
 *
 * It is line level because a plan can pay a frame allowance, pay a lens allowance and exclude
 * coatings in the same order; an order-level percentage cannot say that.
 */
export function allocateCoverage(input) {
    const { plan, eligibility, authorisation, lines } = input;
    if (lines.length === 0)
        throw new ClaimError('NO_LINES', 'An order with no lines has nothing to split.');
    const seenCategories = new Set();
    for (const entry of plan.coverage) {
        if (seenCategories.has(entry.category)) {
            throw new ClaimError('PLAN_AMBIGUOUS', `Plan ${plan.id} covers category ${entry.category} twice.`);
        }
        seenCategories.add(entry.category);
        validateRule(entry.rule);
    }
    // Both of these are whitelists rather than tests against one value, for the `ARC-020` reason in
    // `validateRule`: a status or a policy this build does not know must refuse the split. Written as
    // `status === 'unverified' && policy === 'require_verified'` an unknown status or a mistyped
    // policy would fail open and hand the payer a share nobody authorised.
    if (eligibility.status !== 'verified' &&
        eligibility.status !== 'unverified' &&
        eligibility.status !== 'ineligible') {
        throw new ClaimError('ELIGIBILITY_UNKNOWN', `Eligibility status ${JSON.stringify(eligibility.status)} is not verified, unverified or ineligible.`);
    }
    if (plan.eligibilityPolicy !== 'require_verified' &&
        plan.eligibilityPolicy !== 'allow_unverified') {
        throw new ClaimError('PLAN_POLICY_UNKNOWN', `Plan ${plan.id} has eligibility policy ${JSON.stringify(plan.eligibilityPolicy)}, which is neither require_verified nor allow_unverified.`);
    }
    if (eligibility.status === 'ineligible') {
        throw new ClaimError('NOT_ELIGIBLE', "The customer is not eligible under this plan; the whole order is the customer's.");
    }
    // Safe as an equality test only because the two whitelists above have already refused everything
    // outside these value sets.
    if (eligibility.status === 'unverified' && plan.eligibilityPolicy === 'require_verified') {
        throw new ClaimError('ELIGIBILITY_UNVERIFIED', `Plan ${plan.id} requires eligibility to be verified before a split.`);
    }
    if (plan.authorisationRequired &&
        (authorisation === undefined || authorisation.reference.trim() === '')) {
        throw new ClaimError('AUTHORISATION_REQUIRED', `Plan ${plan.id} requires an authorisation reference.`);
    }
    const seenLines = new Set();
    const allocated = lines.map((line) => {
        if (seenLines.has(line.lineId))
            throw new ClaimError('LINE_DUPLICATE', `Line ${line.lineId} appears twice.`);
        seenLines.add(line.lineId);
        if (line.price.currency !== plan.currency) {
            throw new ClaimError('CURRENCY_MISMATCH', `Line ${line.lineId} is ${line.price.currency}, the plan is ${plan.currency}.`);
        }
        const rule = plan.coverage.find((entry) => entry.category === line.category)
            ?.rule ?? { kind: 'excluded' };
        const payer = payerShareOf(rule, line.price.minor);
        return {
            lineId: line.lineId,
            category: line.category,
            price: line.price,
            payer: money(payer, plan.currency),
            customer: money(line.price.minor - payer, plan.currency),
            rule,
        };
    });
    const sum = (pick) => allocated.reduce((s, l) => s + pick(l), 0n);
    return {
        planId: plan.id,
        payerId: plan.payerId,
        currency: plan.currency,
        lines: allocated,
        payer: money(sum((l) => l.payer.minor), plan.currency),
        customer: money(sum((l) => l.customer.minor), plan.currency),
        total: money(sum((l) => l.price.minor), plan.currency),
        ...(authorisation !== undefined ? { authorisationReference: authorisation.reference } : {}),
    };
}
/**
 * Add two balances owed by the same debtor. Different debtors are refused: a panel's receivable and a
 * customer's udhaar must never become one number.
 */
export function combineBalances(a, b) {
    if (a.debtor.kind !== b.debtor.kind || a.debtor.id !== b.debtor.id) {
        throw new ClaimError('DEBTORS_MIXED', `Cannot add what ${a.debtor.kind} ${a.debtor.id} owes to what ${b.debtor.kind} ${b.debtor.id} owes.`);
    }
    if (a.amount.currency !== b.amount.currency) {
        throw new ClaimError('CURRENCY_MISMATCH', `Cannot combine ${a.amount.currency} with ${b.amount.currency}.`);
    }
    return { debtor: a.debtor, amount: money(a.amount.minor + b.amount.minor, a.amount.currency) };
}
/** One balance per debtor, in first-seen order. There is deliberately no grand total. */
export function groupByDebtor(balances) {
    const grouped = new Map();
    for (const balance of balances) {
        const key = `${balance.debtor.kind}:${balance.debtor.id}`;
        const existing = grouped.get(key);
        grouped.set(key, existing === undefined ? balance : combineBalances(existing, balance));
    }
    return [...grouped.values()];
}
/**
 * What an order leaves owing, as two separate debts.
 *
 * `customerPaid` is what the customer actually handed over. The panel's share is not in it. A
 * customer who "paid" more than their own share has had the panel's portion counted as tender, which
 * would make the panel's receivable vanish, so it is refused.
 */
export function splitOrderDebts(allocation, customerId, customerPaid) {
    if (customerPaid.currency !== allocation.currency) {
        throw new ClaimError('CURRENCY_MISMATCH', `Payment is ${customerPaid.currency}, the order is ${allocation.currency}.`);
    }
    if (customerPaid.minor < 0n)
        throw new ClaimError('PAYMENT_NEGATIVE', 'A payment cannot be negative.');
    if (customerPaid.minor > allocation.customer.minor) {
        throw new ClaimError('CUSTOMER_OVERPAID', 'The customer paid more than their own share. The panel share is a receivable, not a payment, and cannot be tendered at the counter.');
    }
    return {
        panel: { debtor: { kind: 'panel', id: allocation.payerId }, amount: allocation.payer },
        customer: {
            debtor: { kind: 'customer', id: customerId },
            amount: money(allocation.customer.minor - customerPaid.minor, allocation.currency),
        },
    };
}
// ---------------------------------------------------------------------------------------------
// CLM-004: the claim and its state machine
// ---------------------------------------------------------------------------------------------
export const CLAIM_STATUSES = [
    /** Split captured, not sent. `CLM-006`: a valid resting state. */
    'captured',
    'submitted',
    'partially_paid',
    /** Something was refused and nobody has decided what to do with it yet. */
    'denied',
    'paid',
    /** Closed with part or all written off. */
    'written_off',
    /** Closed with refused money moved to the customer (§15.20 failure branch). */
    'transferred',
    /** Everything claimed has been taken back (`CLM-005`). */
    'reversed',
];
/**
 * Total, as `state/transition.ts` asks. The spec defines no claim table (Appendix C has none), so
 * this is derived from what the events can actually produce. The closed states keep an edge to
 * `reversed` because a refund can arrive after the payer has paid (`CLM-005`).
 */
export const CLAIM_TRANSITIONS = {
    captured: ['submitted', 'partially_paid', 'denied', 'paid', 'written_off', 'reversed'],
    submitted: ['partially_paid', 'denied', 'paid', 'written_off', 'transferred', 'reversed'],
    // `transferred` was missing here while `submitted` and `denied` both allow it, so paying part of
    // a claim and then transferring the refusal was refused, while transferring first and paying
    // second was allowed. Both orders end in the same buckets, because
    // `claimed = paid + outstanding + denied + transferred + writtenOff + reduced` does not depend on
    // the order the panel's clerk recorded two independent events in. Found by the claims
    // implementer, which pinned the refusal rather than working around it.
    partially_paid: ['denied', 'paid', 'written_off', 'transferred', 'reversed', 'submitted'],
    denied: ['submitted', 'partially_paid', 'paid', 'written_off', 'transferred', 'reversed'],
    // The three closed states each have an edge back, because `reverse_remittance` puts money the
    // bank took back into `outstanding` and a claim with something outstanding is not closed. Which
    // state it lands in is whether anything is still paid: `partially_paid` if so, `submitted` if the
    // whole remittance bounced. `submitted` rather than `captured` because the panel did answer, and
    // `deriveStatus` reads `remittances.length`, which a reversal leaves alone.
    paid: ['reversed', 'partially_paid', 'submitted'],
    written_off: ['paid', 'reversed', 'partially_paid', 'submitted'],
    transferred: ['paid', 'reversed', 'partially_paid', 'submitted'],
    reversed: [],
};
function assertIsoDate(value, what) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    const ms = match === null ? NaN : Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    if (Number.isNaN(ms) || new Date(ms).toISOString().slice(0, 10) !== value) {
        throw new ClaimError('DATE_INVALID', `${what} must be a real YYYY-MM-DD date, got ${JSON.stringify(value)}.`);
    }
}
function dayNumber(value) {
    return Math.round(Date.parse(`${value}T00:00:00Z`) / 86_400_000);
}
/**
 * `CLM-006`: capture the payer split as a claim. Needs no gateway and no submission.
 *
 * A claim for nothing is refused: an allocation where the plan paid zero has no receivable, and a
 * zero row on every statement would be noise that hides the real ones.
 */
export function openClaim(input) {
    const { allocation } = input;
    assertIsoDate(input.capturedOn, 'capturedOn');
    for (const [name, value] of [
        ['id', input.id],
        ['orderId', input.orderId],
        ['customerId', input.customerId],
    ]) {
        if (value.trim() === '')
            throw new ClaimError('ID_EMPTY', `${name} is required.`);
    }
    if (allocation.payer.minor <= 0n) {
        throw new ClaimError('NOTHING_TO_CLAIM', 'The payer pays nothing on this order, so there is no claim.');
    }
    const zeroMoney = money(0n, allocation.currency);
    return {
        id: input.id,
        orderId: input.orderId,
        customerId: input.customerId,
        panelId: allocation.payerId,
        planId: allocation.planId,
        capturedOn: input.capturedOn,
        currency: allocation.currency,
        lines: allocation.lines,
        customerShare: allocation.customer,
        claimed: allocation.payer,
        paid: zeroMoney,
        outstanding: allocation.payer,
        denied: zeroMoney,
        transferred: zeroMoney,
        writtenOff: zeroMoney,
        reduced: zeroMoney,
        owedToPayer: zeroMoney,
        status: 'captured',
        submissions: [],
        remittances: [],
        denials: [],
        rebills: [],
        ...(allocation.authorisationReference !== undefined
            ? { authorisationReference: allocation.authorisationReference }
            : {}),
    };
}
/**
 * The event kinds this build understands. Exported because `packages/db` registers the same list
 * (Appendix E does not) and its header says the value sets mirror this file.
 */
export const CLAIM_EVENT_KINDS = [
    'submit',
    'remit',
    'resubmit',
    'transfer_to_customer',
    'write_off',
    'reduce',
    'reverse_remittance',
];
function deriveStatus(c) {
    if (c.reduced.minor === c.claimed.minor)
        return 'reversed';
    if (c.outstanding.minor + c.denied.minor === 0n) {
        if (c.writtenOff.minor > 0n)
            return 'written_off';
        if (c.transferred.minor > 0n)
            return 'transferred';
        return 'paid';
    }
    if (c.denied.minor > 0n)
        return 'denied';
    if (c.paid.minor > 0n)
        return 'partially_paid';
    // A claim the panel has answered is no longer merely captured, even if it was never formally sent.
    // This is reachable with zero submissions: refuse part of a captured claim, move the refusal to
    // the customer, and what is left is `submitted` on a claim that was never sent. Dropping the
    // remittance test instead would make that same step `denied` to `captured`, which the table
    // forbids. `packages/db`'s `claim_status_matches_buckets` reads `submission_count` only, so it
    // would demand `captured` for that same claim and refuse the row. That check needs a
    // `remittance_count` term, or this module and the schema disagree on a reachable state.
    return c.submissions.length > 0 || c.remittances.length > 0 ? 'submitted' : 'captured';
}
function amountIn(claim, m, what) {
    if (m.currency !== claim.currency) {
        throw new ClaimError('CURRENCY_MISMATCH', `${what} is ${m.currency}, the claim is ${claim.currency}.`);
    }
    if (m.minor < 0n)
        throw new ClaimError('AMOUNT_NEGATIVE', `${what} cannot be negative.`);
    return m.minor;
}
/**
 * Apply one event. Returns a new claim and nothing is ever deleted: a payer's refusal, a rebill and
 * a write-off all stay on the record.
 */
export function applyClaimEvent(claim, event, policy = {}) {
    // Whitelisted first, for the `ARC-020` reason in `validateRule`: an event kind replayed from a
    // newer schema version must be refused here. Checked before the closed-claim test below, because
    // "this claim is paid" would be a misleading answer to "I do not know that event".
    const known = CLAIM_EVENT_KINDS;
    if (!known.includes(event.kind)) {
        throw new ClaimError('EVENT_UNKNOWN', `No claim event of kind ${JSON.stringify(event.kind)} exists in this build.`);
    }
    assertIsoDate(event.on, 'event date');
    const cur = claim.currency;
    const mk = (n) => money(n, cur);
    const noEffects = {
        customerReceivableCreated: mk(0n),
        writtenOff: mk(0n),
        owedToPayer: mk(0n),
        customerReceivableCancelled: mk(0n),
        writeOffCancelled: mk(0n),
    };
    const open = claim.outstanding.minor + claim.denied.minor;
    // A refund and a bounced cheque are the two facts that arrive after a claim has closed. A
    // cheque that did not clear is the bank's statement, not the shop's choice, and refusing it here
    // would leave `paid` overstating what the panel paid on exactly the claims a closed status makes
    // nobody look at again.
    if (open === 0n && event.kind !== 'reduce' && event.kind !== 'reverse_remittance') {
        throw new ClaimError('CLAIM_CLOSED', `Claim ${claim.id} is ${claim.status}; only a refund can still touch it.`);
    }
    let next;
    let effects = noEffects;
    switch (event.kind) {
        case 'submit': {
            if (claim.status !== 'captured') {
                throw new ClaimError('NOT_CAPTURED', `Only a captured claim is submitted; ${claim.id} is ${claim.status}. Use resubmit for a denial.`);
            }
            next = {
                ...claim,
                submissions: [
                    ...claim.submissions,
                    {
                        on: event.on,
                        attempt: 1,
                        ...(event.reference !== undefined ? { reference: event.reference } : {}),
                    },
                ],
            };
            break;
        }
        case 'remit': {
            const paid = amountIn(claim, event.paid, 'Paid');
            const denied = event.denied === undefined ? 0n : amountIn(claim, event.denied, 'Denied');
            if (paid + denied === 0n)
                throw new ClaimError('REMITTANCE_EMPTY', 'A remittance pays or refuses something.');
            if (paid + denied > claim.outstanding.minor) {
                throw new ClaimError('REMITTANCE_EXCEEDS', `The payer answered for ${paid + denied}, only ${claim.outstanding.minor} is outstanding.`);
            }
            if (denied > 0n && (event.denial === undefined || event.denial.code.trim() === '')) {
                throw new ClaimError('DENIAL_REASON_REQUIRED', "A refused amount needs the payer's reason recorded (§15.20).");
            }
            next = {
                ...claim,
                paid: mk(claim.paid.minor + paid),
                denied: mk(claim.denied.minor + denied),
                outstanding: mk(claim.outstanding.minor - paid - denied),
                remittances: [
                    ...claim.remittances,
                    {
                        on: event.on,
                        paid: mk(paid),
                        denied: mk(denied),
                        ...(event.reference !== undefined ? { reference: event.reference } : {}),
                    },
                ],
                denials: denied > 0n && event.denial !== undefined
                    ? [...claim.denials, { on: event.on, amount: mk(denied), reason: event.denial }]
                    : claim.denials,
            };
            break;
        }
        case 'resubmit': {
            const amount = event.amount === undefined ? claim.denied.minor : amountIn(claim, event.amount, 'Rebill');
            if (amount <= 0n || amount > claim.denied.minor) {
                throw new ClaimError('REBILL_AMOUNT', `A rebill is more than zero and at most the ${claim.denied.minor} denied.`);
            }
            if (policy.maxResubmissions !== undefined &&
                claim.rebills.length >= policy.maxResubmissions) {
                throw new ClaimError('REBILL_LIMIT', `Claim ${claim.id} has already been rebilled ${claim.rebills.length} times.`);
            }
            const attempt = claim.rebills.length + 2;
            next = {
                ...claim,
                denied: mk(claim.denied.minor - amount),
                outstanding: mk(claim.outstanding.minor + amount),
                rebills: [...claim.rebills, { on: event.on, amount: mk(amount), attempt }],
                submissions: [...claim.submissions, { on: event.on, attempt }],
            };
            break;
        }
        case 'transfer_to_customer': {
            const amount = event.amount === undefined ? claim.denied.minor : amountIn(claim, event.amount, 'Transfer');
            if (amount <= 0n || amount > claim.denied.minor) {
                throw new ClaimError('TRANSFER_AMOUNT', `Only denied money moves to the customer: more than zero, at most ${claim.denied.minor}.`);
            }
            if (event.reason.trim() === '') {
                throw new ClaimError('REASON_REQUIRED', 'The customer is contacted with the reason, so one is required.');
            }
            next = {
                ...claim,
                denied: mk(claim.denied.minor - amount),
                transferred: mk(claim.transferred.minor + amount),
            };
            effects = { ...noEffects, customerReceivableCreated: mk(amount) };
            break;
        }
        case 'write_off': {
            const amount = event.amount === undefined ? open : amountIn(claim, event.amount, 'Write-off');
            if (amount <= 0n || amount > open) {
                throw new ClaimError('WRITE_OFF_AMOUNT', `A write-off is more than zero and at most the ${open} still open.`);
            }
            if (event.reason.trim() === '')
                throw new ClaimError('REASON_REQUIRED', 'A write-off needs a reason.');
            if (event.approvedBy.trim() === '')
                throw new ClaimError('APPROVER_REQUIRED', 'A write-off needs a named approver.');
            // Refused money is the usual candidate, so it goes first; what the payer never answered is next.
            const fromDenied = amount < claim.denied.minor ? amount : claim.denied.minor;
            next = {
                ...claim,
                denied: mk(claim.denied.minor - fromDenied),
                outstanding: mk(claim.outstanding.minor - (amount - fromDenied)),
                writtenOff: mk(claim.writtenOff.minor + amount),
            };
            effects = { ...noEffects, writtenOff: mk(amount) };
            break;
        }
        case 'reduce': {
            const amount = amountIn(claim, event.amount, 'Reduction');
            const remaining = claim.claimed.minor - claim.reduced.minor;
            if (amount <= 0n || amount > remaining) {
                throw new ClaimError('REDUCE_AMOUNT', `A reduction is more than zero and at most the ${remaining} still standing.`);
            }
            if (event.reason.trim() === '')
                throw new ClaimError('REASON_REQUIRED', 'A reduction needs a reason.');
            // Money that has not moved is taken first. Cash already received is taken last because it is
            // the one that obliges the shop to pay something back.
            let left = amount;
            const take = (balance) => {
                const taken = left < balance ? left : balance;
                left -= taken;
                return taken;
            };
            const fromOutstanding = take(claim.outstanding.minor);
            const fromDenied = take(claim.denied.minor);
            const fromTransferred = take(claim.transferred.minor);
            const fromWrittenOff = take(claim.writtenOff.minor);
            const fromPaid = take(claim.paid.minor);
            next = {
                ...claim,
                outstanding: mk(claim.outstanding.minor - fromOutstanding),
                denied: mk(claim.denied.minor - fromDenied),
                transferred: mk(claim.transferred.minor - fromTransferred),
                writtenOff: mk(claim.writtenOff.minor - fromWrittenOff),
                paid: mk(claim.paid.minor - fromPaid),
                reduced: mk(claim.reduced.minor + amount),
                owedToPayer: mk(claim.owedToPayer.minor + fromPaid),
            };
            effects = {
                ...noEffects,
                owedToPayer: mk(fromPaid),
                customerReceivableCancelled: mk(fromTransferred),
                writeOffCancelled: mk(fromWrittenOff),
            };
            break;
        }
        case 'reverse_remittance': {
            const amount = amountIn(claim, event.amount, 'Reversal');
            if (amount <= 0n || amount > claim.paid.minor) {
                throw new ClaimError('REVERSAL_AMOUNT', `A reversal is more than zero and at most the ${claim.paid.minor} this claim has been paid.`);
            }
            if (event.reason.trim() === '') {
                throw new ClaimError('REASON_REQUIRED', 'A reversal says why the money came back, because the panel will be asked about it.');
            }
            // Straight back to `outstanding`: the panel owes it again and has not refused it, so it is
            // not `denied`. `owedToPayer` is untouched, which is the distinction worth keeping: a refund
            // obliges the shop to hand cash back, a bounced cheque means the cash never arrived.
            next = {
                ...claim,
                paid: mk(claim.paid.minor - amount),
                outstanding: mk(claim.outstanding.minor + amount),
            };
            break;
        }
    }
    const status = deriveStatus(next);
    if (status !== claim.status && !canTransition(CLAIM_TRANSITIONS, claim.status, status)) {
        throw new ClaimError('CLAIM_TRANSITION', `${claim.status} to ${status} is not an allowed claim transition.`);
    }
    return { claim: { ...next, status }, effects };
}
/** The invariant the amounts keep. Cheap enough to assert at any boundary. */
export function claimBalances(claim) {
    return (claim.paid.minor +
        claim.outstanding.minor +
        claim.denied.minor +
        claim.transferred.minor +
        claim.writtenOff.minor +
        claim.reduced.minor ===
        claim.claimed.minor);
}
/** What the panel still owes on a claim: unanswered plus refused-and-undecided. */
export function claimOpenAmount(claim) {
    return money(claim.outstanding.minor + claim.denied.minor, claim.currency);
}
/**
 * Reverse both sides of a refund.
 *
 * The rule frozen on the line is re-run against the price the customer keeps, and each side is
 * refunded the difference between its old and new share. So a 300 frame with a 100 allowance
 * refunded by 50 costs the customer 50 and the payer nothing, while a full refund returns 100 to the
 * payer and 200 to the customer. Every rule's shares only fall as the price falls, so neither side
 * is ever asked to pay in. Differences telescope across repeated partial refunds, so the sum of
 * every refund equals the whole.
 */
export function reverseAllocation(allocation, refunds) {
    const seen = new Set();
    const lines = refunds.map((refund) => {
        if (seen.has(refund.lineId)) {
            throw new ClaimError('LINE_DUPLICATE', `Line ${refund.lineId} is refunded twice in one call.`);
        }
        seen.add(refund.lineId);
        const line = allocation.lines.find((l) => l.lineId === refund.lineId);
        if (line === undefined)
            throw new ClaimError('LINE_UNKNOWN', `Line ${refund.lineId} is not on this order.`);
        const toDate = refund.refundedToDate;
        const before = refund.previouslyRefunded ?? money(0n, allocation.currency);
        if (toDate.currency !== allocation.currency || before.currency !== allocation.currency) {
            throw new ClaimError('CURRENCY_MISMATCH', 'A refund must be in the order currency.');
        }
        if (before.minor < 0n || toDate.minor < before.minor) {
            throw new ClaimError('REFUND_INVALID', 'Refunds to date cannot fall below what was already refunded.');
        }
        if (toDate.minor > line.price.minor) {
            throw new ClaimError('REFUND_EXCEEDS_LINE', `Line ${line.lineId} cannot be refunded more than its ${line.price.minor}.`);
        }
        const payerThen = payerShareOf(line.rule, line.price.minor - before.minor);
        const payerNow = payerShareOf(line.rule, line.price.minor - toDate.minor);
        const customerThen = line.price.minor - before.minor - payerThen;
        const customerNow = line.price.minor - toDate.minor - payerNow;
        const payerReversal = payerThen - payerNow;
        const customerRefund = customerThen - customerNow;
        // No input can reach this with any of the five shapes: each one's payer share and customer share
        // are both monotone non-decreasing in price, which is what makes the two reversals add to exactly
        // the refund. It is kept because the shapes are a growing list and a non-monotone one (a tiered
        // allowance, say) would otherwise silently hand money back twice. `claims.test.ts` asserts the
        // monotonicity directly, so a new shape that breaks it goes red in a test rather than relying on
        // this line to notice at a shop counter.
        if (payerReversal < 0n || customerRefund < 0n) {
            throw new ClaimError('REFUND_NEGATIVE', 'A refund cannot make either side pay in.');
        }
        return {
            lineId: line.lineId,
            payerReversal: money(payerReversal, allocation.currency),
            customerRefund: money(customerRefund, allocation.currency),
        };
    });
    const payer = lines.reduce((s, l) => s + l.payerReversal.minor, 0n);
    const customer = lines.reduce((s, l) => s + l.customerRefund.minor, 0n);
    return {
        lines,
        payerReversal: money(payer, allocation.currency),
        customerRefund: money(customer, allocation.currency),
        claimReversalRequired: payer > 0n,
    };
}
/**
 * The data a printed claim form needs. The PRD gives neither a field list nor a layout, so this is
 * the split itself and the identifiers that tie it to an order. Rendering belongs to the print layer.
 */
export function claimFormData(claim) {
    return {
        claimId: claim.id,
        orderId: claim.orderId,
        customerId: claim.customerId,
        panelId: claim.panelId,
        planId: claim.planId,
        capturedOn: claim.capturedOn,
        ...(claim.authorisationReference !== undefined
            ? { authorisationReference: claim.authorisationReference }
            : {}),
        lines: claim.lines.map((l) => ({
            lineId: l.lineId,
            category: l.category,
            price: l.price,
            panelPays: l.payer,
            customerPays: l.customer,
            basis: l.rule.kind,
        })),
        orderTotal: money(claim.lines.reduce((s, l) => s + l.price.minor, 0n), claim.currency),
        panelTotal: money(claim.lines.reduce((s, l) => s + l.payer.minor, 0n), claim.currency),
        customerTotal: money(claim.lines.reduce((s, l) => s + l.customer.minor, 0n), claim.currency),
    };
}
/**
 * The inclusive day bounds of a calendar month, `YYYY-MM` to `YYYY-MM-01` and its last day.
 *
 * §15.20 step 4 and `PK-028` say monthly, and a statement whose period a shopkeeper types by hand
 * is a statement that can run 1 September to 15 October and bill half of September's claims twice.
 * `periodOfDate` in `branch-date.ts` produces the same `YYYY-MM` from a branch's own day, so the
 * two spellings of a month agree.
 */
export function statementMonth(month) {
    const match = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(month);
    if (match === null) {
        throw new ClaimError('PERIOD_NOT_A_MONTH', `${JSON.stringify(month)} is not a month. Write it as 2026-09 (PK-028, §15.20 step 4).`);
    }
    const year = Number(match[1]);
    const index = Number(match[2]);
    // Day 0 of the following month is the last day of this one, so February and leap years need no
    // table. `Date.UTC` is the only clock here and the result is a calendar day, never an instant.
    const lastDay = new Date(Date.UTC(year, index, 0)).getUTCDate();
    return { from: `${month}-01`, to: `${month}-${String(lastDay).padStart(2, '0')}` };
}
const byDateThenId = (a, b) => a.capturedOn === b.capturedOn
    ? a.id < b.id
        ? -1
        : a.id > b.id
            ? 1
            : 0
    : a.capturedOn < b.capturedOn
        ? -1
        : 1;
/**
 * Appendix Z "Panel statement": panel, orders, panel share, customer share, paid, outstanding.
 *
 * Appendix Z's one "outstanding" column cannot carry the whole panel side, and a statement whose
 * columns do not add up is the fastest way to lose an argument with a panel's accounts clerk. So
 * the money the panel has not paid is split across the four states it can actually be in, and every
 * row satisfies `panelShare = paid + outstanding + denied + transferred + writtenOff`. Appendix Z
 * should list those columns.
 *
 * `packages/db`'s `panel_statement` and `panel_statement_line` carried only panel share, customer
 * share, paid, outstanding and denied, so a stored line could not add up once a claim was
 * transferred or written off, and that is why `issueStatement` was not built. Migration
 * `9028_a_statement_carries_what_happened_to_the_money.sql` adds the two columns and makes the
 * identity above a check constraint on both tables, so the stored statement reconciles for the
 * same reason this function's output does.
 *
 * `customerShare` is as captured and is never added into a panel figure. A later refund reduces the
 * panel side through `reduced` but does not shrink this column, so a reader comparing it against the
 * order needs the order record (`CLM-005` gives no customer-side reduction field).
 */
export function buildPanelStatement(input) {
    assertIsoDate(input.from, 'from');
    assertIsoDate(input.to, 'to');
    if (input.from > input.to)
        throw new ClaimError('PERIOD_INVALID', 'The statement period ends before it starts.');
    const cur = input.currency;
    const mk = (n) => money(n, cur);
    const rows = input.claims
        .filter((c) => c.panelId === input.panelId && c.capturedOn >= input.from && c.capturedOn <= input.to)
        .sort(byDateThenId)
        .map((c) => {
        if (c.currency !== cur) {
            throw new ClaimError('CURRENCY_MISMATCH', `Claim ${c.id} is ${c.currency}, the statement is ${cur}.`);
        }
        return {
            claimId: c.id,
            orderId: c.orderId,
            customerId: c.customerId,
            capturedOn: c.capturedOn,
            status: c.status,
            panelShare: mk(c.claimed.minor - c.reduced.minor),
            customerShare: c.customerShare,
            paid: c.paid,
            outstanding: c.outstanding,
            denied: c.denied,
            transferred: c.transferred,
            writtenOff: c.writtenOff,
        };
    });
    const total = (pick) => mk(rows.reduce((s, r) => s + pick(r), 0n));
    return {
        panelId: input.panelId,
        from: input.from,
        to: input.to,
        currency: cur,
        rows,
        totals: {
            panelShare: total((r) => r.panelShare.minor),
            customerShare: total((r) => r.customerShare.minor),
            paid: total((r) => r.paid.minor),
            outstanding: total((r) => r.outstanding.minor),
            denied: total((r) => r.denied.minor),
            transferred: total((r) => r.transferred.minor),
            writtenOff: total((r) => r.writtenOff.minor),
        },
    };
}
/** Every row and the totals add up. The column set exists so that this can be true. */
export function statementReconciles(statement) {
    const adds = (r) => r.paid.minor +
        r.outstanding.minor +
        r.denied.minor +
        r.transferred.minor +
        r.writtenOff.minor ===
        r.panelShare.minor;
    return statement.rows.every(adds) && adds(statement.totals);
}
/**
 * §15.20 step 5: spread one panel payment across its claims. Applies only to `outstanding`, never to
 * denied money, which the panel has said it will not pay. Feed each allocation to a `remit` event.
 */
export function allocatePanelPayment(panelId, payment, claims, plan) {
    if (payment.minor <= 0n)
        throw new ClaimError('PAYMENT_NOT_POSITIVE', 'A payment is more than zero.');
    const own = claims.filter((c) => c.panelId === panelId);
    for (const c of own) {
        if (c.currency !== payment.currency) {
            throw new ClaimError('CURRENCY_MISMATCH', `Claim ${c.id} is ${c.currency}, the payment is ${payment.currency}.`);
        }
    }
    const mk = (n) => money(n, payment.currency);
    if (plan.kind === 'oldest_first') {
        const ordered = own.filter((c) => c.outstanding.minor > 0n).sort(byDateThenId);
        let left = payment.minor;
        const allocations = [];
        for (const c of ordered) {
            if (left === 0n)
                break;
            const amount = left < c.outstanding.minor ? left : c.outstanding.minor;
            allocations.push({ claimId: c.id, amount: mk(amount) });
            left -= amount;
        }
        return { allocations, unapplied: mk(left) };
    }
    const seen = new Set();
    let used = 0n;
    const allocations = plan.allocations.map((a) => {
        if (seen.has(a.claimId))
            throw new ClaimError('CLAIM_DUPLICATE', `Claim ${a.claimId} appears twice.`);
        seen.add(a.claimId);
        const claim = own.find((c) => c.id === a.claimId);
        if (claim === undefined)
            throw new ClaimError('CLAIM_UNKNOWN', `Claim ${a.claimId} is not this panel's.`);
        if (a.amount.currency !== payment.currency) {
            throw new ClaimError('CURRENCY_MISMATCH', 'Allocation currency differs from the payment.');
        }
        if (a.amount.minor <= 0n || a.amount.minor > claim.outstanding.minor) {
            throw new ClaimError('ALLOCATION_AMOUNT', `Claim ${claim.id} can take more than zero and at most ${claim.outstanding.minor}.`);
        }
        used += a.amount.minor;
        return { claimId: a.claimId, amount: a.amount };
    });
    if (used > payment.minor)
        throw new ClaimError('ALLOCATION_EXCEEDS_PAYMENT', 'More is allocated than was received.');
    return { allocations, unapplied: mk(payment.minor - used) };
}
/**
 * `CLM-004`'s aged claims report.
 *
 * Deliberately across every panel at once: the buckets are the shop's total exposure and each row
 * carries its `panelId` so a caller can group. That is not the "two debtors, never one balance" rule
 * being bent, which is about a panel and a customer; two panels are the same kind of debtor and the
 * same remedy. A per-panel report is `buildPanelStatement`.
 */
export function agedClaims(input) {
    assertIsoDate(input.asOf, 'asOf');
    const bounds = input.bucketBoundsDays;
    if (bounds.length === 0)
        throw new ClaimError('BUCKETS_INVALID', 'Ageing needs at least one bucket bound.');
    bounds.forEach((b, i) => {
        const previous = i === 0 ? 0 : (bounds[i - 1] ?? 0);
        if (!Number.isInteger(b) || b <= previous) {
            throw new ClaimError('BUCKETS_INVALID', 'Bucket bounds are whole days, positive and strictly ascending.');
        }
    });
    const mk = (n) => money(n, input.currency);
    const labels = [
        ...bounds.map((b, i) => `${i === 0 ? 0 : (bounds[i - 1] ?? 0) + 1}-${b}`),
        `${(bounds[bounds.length - 1] ?? 0) + 1}+`,
    ];
    const counts = labels.map(() => 0);
    const sums = labels.map(() => 0n);
    let unsubmittedCount = 0;
    let unsubmittedSum = 0n;
    const rows = [];
    for (const c of input.claims) {
        const open = c.outstanding.minor + c.denied.minor;
        if (open === 0n)
            continue;
        if (c.currency !== input.currency) {
            throw new ClaimError('CURRENCY_MISMATCH', `Claim ${c.id} is ${c.currency}, the report is ${input.currency}.`);
        }
        const origin = input.ageFrom === 'captured' ? c.capturedOn : c.submissions[0]?.on;
        if (origin === undefined) {
            unsubmittedCount += 1;
            unsubmittedSum += open;
            rows.push({ claimId: c.id, panelId: c.panelId, open: mk(open) });
            continue;
        }
        const age = dayNumber(input.asOf) - dayNumber(origin);
        if (age < 0)
            throw new ClaimError('DATE_ORDER', `Claim ${c.id} is dated after the report date.`);
        let bucket = bounds.findIndex((b) => age <= b);
        if (bucket === -1)
            bucket = bounds.length;
        counts[bucket] = (counts[bucket] ?? 0) + 1;
        sums[bucket] = (sums[bucket] ?? 0n) + open;
        rows.push({ claimId: c.id, panelId: c.panelId, open: mk(open), ageDays: age, bucket });
    }
    return {
        buckets: labels.map((label, i) => ({
            label,
            claimCount: counts[i] ?? 0,
            open: mk(sums[i] ?? 0n),
        })),
        notYetSubmitted: { claimCount: unsubmittedCount, open: mk(unsubmittedSum) },
        rows,
    };
}
//# sourceMappingURL=claims.js.map